import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthCallback — handles the redirect from the hosted Google OAuth provider.
 *
 * The provider redirects the browser to `${origin}/auth/callback#session_id=...`.
 * This component:
 *   1. Parses session_id from the URL fragment (synchronously, see App.js).
 *   2. POSTs it to /api/auth/google/session — backend exchanges it with the
 *      hosted OAuth session-data endpoint for the user's Google identity,
 *      mints our brand JWT, and sets the access_token cookie.
 *   3. Hydrates AuthContext via refreshUser() and navigates onward.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallbackPage() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError('Missing session id from auth provider.');
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/api/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        // Wipe the fragment so a refresh doesn't re-fire.
        window.history.replaceState({}, document.title, window.location.pathname);
        await refreshUser();
        nav(data?.onboarding_complete === false ? '/onboarding' : '/home', { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || e.message || 'Sign-in failed';
        setError(typeof msg === 'string' ? msg : 'Sign-in failed');
      }
    })();
  }, [nav, refreshUser]);

  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="auth-callback-page">
      <div className="text-center space-y-4">
        {!error ? (
          <>
            <Loader2 className="h-7 w-7 mx-auto animate-spin text-[#00D4FF]" />
            <p className="text-sm text-gray-400">Signing you in with Google…</p>
          </>
        ) : (
          <>
            <div className="text-3xl">⚠️</div>
            <p className="text-sm text-red-400" data-testid="auth-callback-error">{error}</p>
            <button
              onClick={() => nav('/login', { replace: true })}
              data-testid="auth-callback-back-btn"
              className="mt-2 px-4 py-2 rounded-lg border border-[#1f1f1f] text-xs hover:border-[#FF4D94] transition"
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
